import { z } from 'zod';
import { AssetSchema } from '../shared/asset.schema';
export const IdeationWidgetKeyV1 = 'ideation-widget:v1';
export const IdeationWidgetKindV1 = 'ideation';
export const CoreTaskSchemaV1 = z
    .enum(['All in', 'Stunt', 'AO/Quick Wins', 'IRL Activations'])
    .describe('Type of briefing or task');
export const IdeationKeyStrategicElementsSchemaV1 = z.object({
    title: z
        .enum(['Core Tension', 'Key Message', 'Audience'])
        .describe('The title of the key strategic element'),
    content: z.string().describe('The content of the key strategic element'),
});
export const IdeationConstraintsSchemaV1 = z.object({
    title: z.string().describe('The title of the constraint'),
    content: z.string().describe('The content of the constraint'),
});
export const IdeationBenchmarkReferenceSchemaV1 = z.object({
    type: z.enum(['cliches', 'disruptive-stunt']).describe('The creative archetype of the campaign'),
    title: z.string().describe('The commonly recognized name or title of the campaign.'),
    description: z
        .string()
        .describe('A very short concise summary of what the campaign did, how it worked, and its core creative approach.'),
    universalTruth: z
        .string()
        .describe('The foundational human insight or emotional truth that the idea is built on'),
    brand: z.string().describe('The brand, organization, or entity behind the campaign.'),
    agency: z
        .string()
        .describe('The creative agency or team responsible for developing the campaign.'),
    year: z.string().describe('The year the campaign launched.'),
    link: z.string().describe('Verifiable link to the campaign'),
});
export const IdeationSummarySchemaV1 = z.object({
    creativeMission: z.string().describe('The creative mission of the ideation widget'),
    keyStrategicElements: z
        .array(IdeationKeyStrategicElementsSchemaV1)
        .describe('The key strategic elements of the ideation widget')
        .min(3)
        .max(3),
    constraints: z
        .array(IdeationConstraintsSchemaV1)
        .min(1)
        .max(3)
        .describe('The constraints of the ideation widget'),
});
export const IdeationBenchmarkReferenceWithImageSchemaV1 = z.object({
    ...IdeationBenchmarkReferenceSchemaV1.shape,
    image: AssetSchema.describe('The image of the benchmark reference'),
});
export const IdeationExecutionSchemaV1 = z.object({
    title: z.string().describe('The title of the execution'),
    description: z.string().describe('The description of the execution'),
    platform: z.string().describe('The platform of the execution'),
});
export const IdeationExecutionWithImagesSchemaV1 = z.object({
    ...IdeationExecutionSchemaV1.shape,
    image: AssetSchema.describe('The image of the execution'),
});
export const IdeationAudienceScoreSchemaV1 = z.object({
    title: z
        .enum(['General', 'Emotional', 'Cultural', 'Purpose', 'Sustained interest'])
        .describe('The title of the score'),
    score: z.number().int().min(0).max(100),
    description: z.string().describe('The description of the score'),
});
export const IdeationAudienceScoreListSchemaV1 = z
    .array(IdeationAudienceScoreSchemaV1)
    .length(5)
    .refine((scores) => {
    const titles = scores.map((s) => s.title);
    const expectedTitles = ['General', 'Emotional', 'Cultural', 'Purpose', 'Sustained interest'];
    return expectedTitles.every((title) => titles.includes(title));
}, {
    message: 'Must contain exactly one score for each title: General, Emotional, Cultural, Purpose, Sustained interest',
});
export const IdeationCreativeScoreSchemaV1 = z.object({
    title: z
        .enum(['General', 'Innovation', 'Creativity', 'Longevity', 'Risk taking'])
        .describe('The title of the score'),
    score: z.number().int().min(0).max(100),
    description: z.string().describe('The description of the score'),
});
export const IdeationCreativeScoreListSchemaV1 = z
    .array(IdeationCreativeScoreSchemaV1)
    .length(5)
    .refine((scores) => {
    const titles = scores.map((s) => s.title);
    const expectedTitles = ['General', 'Innovation', 'Creativity', 'Longevity', 'Risk taking'];
    return expectedTitles.every((title) => titles.includes(title));
}, {
    message: 'Must contain exactly one score for each title: General, Innovation, Creativity, Longevity, Risk taking',
});
export const IdeationIdeaSchemaV1 = z.object({
    id: z.uuid().describe('The id of the idea'),
    title: z.string().describe('The title of the idea'),
    description: z.string().describe('The description of the idea'),
    provocation: z
        .string()
        .describe('A very short summary of the actionable provocation spark that this idea is based on'),
    whyThisWorks: z.string().describe('The why this works of the idea'),
    executions: z.array(IdeationExecutionSchemaV1).describe('The executions of the idea').length(3),
});
export const IdeationIdeaWithScoreSchemaV1 = z.object({
    ...IdeationIdeaSchemaV1.shape,
    audienceScore: IdeationAudienceScoreListSchemaV1.describe('The audience score of the idea'),
    creativeScore: IdeationCreativeScoreListSchemaV1.describe('The creative score of the idea'),
});
export const IdeationIdeaWithImagesSchemaV1 = z.object({
    ...IdeationIdeaWithScoreSchemaV1.shape,
    image: AssetSchema.describe('The idea image of the idea'),
    executions: z
        .array(IdeationExecutionWithImagesSchemaV1)
        .describe('The executions of the idea')
        .length(3),
});
export const IdeationUnconventionalSparkSchemaV1 = z.object({
    topic: z
        .enum(['Reframe', 'Counter-truth', 'Metaphorical Comparison'])
        .describe('The provocative type'),
    question: z.string().describe('The provocative question'),
    answer: z.string().describe('A concise, actionable answer based on your internal reasoning'),
});
export const IdeationWidgetSchemaV1 = z.object({
    title: z.string().describe('The title of the ideation widget'),
    coreTask: CoreTaskSchemaV1.describe('The core task of the ideation widget'),
    summary: IdeationSummarySchemaV1.describe('The summary of the ideation widget'),
    mainImage: AssetSchema.describe('The main image of the ideation widget'),
    additionalImages: z.array(AssetSchema).describe('The additional images of the ideation widget'),
    references: z
        .array(IdeationBenchmarkReferenceWithImageSchemaV1)
        .describe('The benchmark references'),
    sparks: z.array(IdeationUnconventionalSparkSchemaV1).describe('The unconventional sparks'),
    ideas: z.array(IdeationIdeaWithImagesSchemaV1).describe('The ideas of the ideation widget'),
});
